import NameNumberViewModel from "@/models/Shared/NameNumberViewModel";
import NameStringViewModel from "@/models/Shared/NameStringViewModel";
import DebitCreditCode from '@/models/Shared/DebitCreditCode';
import BankingIntegrations from '@/models/Settings/Institutions/Integrations/BankingIntegrations';
import CardIntegrations from '@/models/Settings/Institutions/Integrations/CardIntegrations';
import TransactionMonitoringIntegrations from '@/models/Settings/Institutions/Integrations/TransactionMonitoringIntegrations';
import DayOfWeek from "@/models/Shared/DayOfWeek";
import NonWorkingDays from "@/models/Shared/NonWorkingDays";
import PlanLength from "@/models/Shared/PlanLength";
import PostingScheduleType from "@/models/Shared/PostingScheduleType";
import PayeeType from '@/models/Accounts/Shared/Payees/PayeeType';
import Month from "@/models/Shared/Month";
import AccountState from "@/models/Accounts/Shared/AccountState";
import AccountStateReasonCode from "@/models/Accounts/Shared/AccountStateReasonCode";
import TransactionsOptions from "@/models/Accounts/Shared/Transactions/TransactionsOptions";
import TransactionsOrderOptions from "@/models/Accounts/Shared/Transactions/TransactionsOrderOptions";
import AppMediumType from "@/models/Settings/AppMediums/AppMediumType";
import ProductType from "@/models/Products/ProductType";
import GeneralLedgerAccountType from "@/models/Accounting/GeneralLedgerAccounts/GeneralLedgerAccountType";
import GeneralLedgerAccountState from "@/models/Accounting/GeneralLedgerAccounts/GeneralLedgerAccountState";
import FeeDefinitionType from "@/models/Products/ProductFees/FeeDefinitionType";
import ProductSubType from "@/models/Products/ProductSubType";
import ProductState from "@/models/Products/ProductState";
import ProductIssueState from '@/models/Products/ProductIssues/ProductIssueState';
import PaymentState from "@/models/Accounts/Shared/Payments/PaymentState";
import ProductFeatureVisiblity from "@/models/Products/ProductFeatureVisiblity";
import TargetCustomerType from "@/models/Products/TargetCustomerType";
import PeriodType from "@/models/Shared/PeriodType";
import ScheduleType from "@/models/Shared/ScheduleType";
import ScheduleDueDatesType from "@/models/Shared/ScheduleDueDatesType";
import ScheduleFrequency from '@/models/Shared/ScheduleFrequency';
import ShortMonthHandlingType from "@/models/Shared/ShortMonthHandlingType";
import PostedFrequencyType from "@/models/Shared/PostedFrequencyType";
import InterestBaseRate from "@/models/Products/InterestBaseRate";
import InterestDaysInYear from "@/models/Products/InterestDaysInYear";
import InterestRateType from "@/models/Products/InterestRateType";
import InterestType from '@/models/Products/InterestType';
import InterestRepaymentType from "@/models/Products/InterestRepaymentType";
import InterestCalculationType from "@/models/Products/InterestCalculationType";
import InterestCalculatedOnBalanceType from "@/models/Products/InterestCalculatedOnBalanceType";
import InterestPostedOnType from "@/models/Products/InterestPostedOnType";
import RepaymentInterestCalculationType from "@/models/Shared/RepaymentInterestCalculationType";
import FixedTermMaturityType from "@/models/Products/ProductSettings/DefaultSettings/FixedTermMaturity/FixedTermMaturityType";
import FundingWindowTriggerType from "@/models/Products/ProductSettings/DefaultSettings/FixedTerm/FundingWindowTriggerType";
import TransactionType from "@/models/Accounts/Shared/Transactions/TransactionType";
import AccountType from '@/models/Shared/AccountType';
import AccountSearchType from '@/models/Shared/AccountSearchType';
import { AuthModule } from '@/store/Auth/AuthModule';

const countries = require('i18n-iso-countries');
countries.registerLocale(require('i18n-iso-countries/langs/en.json'));

export default class ListUtils {

    // General Utils

    static debitCreditOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Debit", DebitCreditCode.debit),
            new NameStringViewModel("Credit", DebitCreditCode.credit),
        ]
    }

    static bankingIntegrationsOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("ClearBank", BankingIntegrations.ClearBank),
            new NameStringViewModel("Swisscom", BankingIntegrations.Swisscom),
        ]
    }

    static cardIntegrationsOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Accomplish", CardIntegrations.Accomplish),
            new NameStringViewModel("GPS (Thredd)", CardIntegrations.GPS),
            new NameStringViewModel("Marqeta", CardIntegrations.Marqeta),
        ]
    }

    static transactionMonitoringIntegrationsOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Comply", TransactionMonitoringIntegrations.Comply),
            new NameStringViewModel("Swisscom", TransactionMonitoringIntegrations.Swisscom),
            new NameStringViewModel("Hawk AI", TransactionMonitoringIntegrations.HawkAI),
        ]
    }

    static compareOutputFormats(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Side-By-Side", "side-by-side"),
            new NameStringViewModel("Line-By-Line", "line-by-line"),
        ]
    }

    static periodTypeOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Day(s)", PeriodType.Day),
            new NameStringViewModel("Week(s)", PeriodType.Week),
            new NameStringViewModel("Month(s)", PeriodType.Month),
            new NameStringViewModel("Quarter(s)", PeriodType.Quarter),
            new NameStringViewModel("Year(s)", PeriodType.Year),
        ]
    }

    static postingScheduleTypeOptions(): NameStringViewModel[] {
        return [
            // Note: Non-Fixed Term Products only allow "Fixed Schedule" at the moment
            new NameStringViewModel("Fixed Schedule", PostingScheduleType.FixedSchedule),
            // new NameStringViewModel("Anniversary Account Open", PostingScheduleType.AnniversaryAccountOpen),
            //new NameStringViewModel("Anniversary First Transaction", PostingScheduleType.AnniversaryFirstTransaction)
        ]
    }

    static postingScheduleTypeOptionsForFixedTerm(): NameStringViewModel[] {
        return [
            // Note: Fixed Term Products only allow "Anniversary First Transaction" at the moment
            // new NameStringViewModel("Fixed Schedule", PostingScheduleType.FixedSchedule),
            // new NameStringViewModel("Anniversary Account Open", PostingScheduleType.AnniversaryAccountOpen),
            new NameStringViewModel("Anniversary First Transaction", PostingScheduleType.AnniversaryFirstTransaction)
        ]
    }

    static accountTypeOptionsShortList(): NameStringViewModel[] {
        const institutionLabels = AuthModule.me.institution.config.labels;
        return [
            new NameStringViewModel("Customer Accounts", AccountType.Customer),
            new NameStringViewModel(institutionLabels.institutionAccounts, AccountType.Institution)
        ]
    }

    static accountTypeOptionsLongList(): NameStringViewModel[] {
        const institutionLabels = AuthModule.me.institution.config.labels;
        return [
            new NameStringViewModel("Customer Accounts", AccountType.Customer),
            new NameStringViewModel(institutionLabels.institutionAccounts, AccountType.Institution),
            new NameStringViewModel(institutionLabels.thirdPartyAccounts, AccountType.ThirdParty)
        ]
    }

    static accountSearchTypeOptions(): NameStringViewModel[] {
        const availablePayeeTypes = AuthModule.me.institution.config.availablePayeeTypes;
        const includesUKPayeeType = availablePayeeTypes.includes(PayeeType.UK);
        const accountIdentifierText = includesUKPayeeType ? "Account Number" : "IBAN";
        return [
            new NameStringViewModel("Account Name", AccountSearchType.AccountName),
            new NameStringViewModel(accountIdentifierText, AccountSearchType.AccountIdentifier),
            new NameStringViewModel("Customer Name", AccountSearchType.CustomerName)
        ]
    }

    static accountStateOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Active", AccountState.Active),
            new NameStringViewModel("Read Only", AccountState.ReadOnly),
            new NameStringViewModel("Closed", AccountState.Closed)
        ]
    }

    static accountStateReasonOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Inactive", AccountStateReasonCode.Inactive),
            new NameStringViewModel("Manual", AccountStateReasonCode.Manual),
            new NameStringViewModel("CASS Switch", AccountStateReasonCode.CassSwitch),
            new NameStringViewModel("Early Breakage", AccountStateReasonCode.EarlyBreakage)
        ]
    }

    static accountStateReasonOptionsForFixedTerm(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Inactive", AccountStateReasonCode.Inactive),
            new NameStringViewModel("Manual", AccountStateReasonCode.Manual),
            new NameStringViewModel("CASS Switch", AccountStateReasonCode.CassSwitch),
            new NameStringViewModel("FTD Matured", AccountStateReasonCode.FtdMatured),
            new NameStringViewModel("FTD Underfunded", AccountStateReasonCode.FtdUnderfunded),
            new NameStringViewModel("Early Breakage", AccountStateReasonCode.EarlyBreakage)
        ]
    }

    static generalLedgerAccountTypeOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Asset", GeneralLedgerAccountType.Asset),
            new NameStringViewModel("Liability", GeneralLedgerAccountType.Liability),
            new NameStringViewModel("Income", GeneralLedgerAccountType.Income),
            new NameStringViewModel("Expense", GeneralLedgerAccountType.Expense),
            new NameStringViewModel("Equity", GeneralLedgerAccountType.Equity)
        ]
    }

    static generalLedgerAccountStateOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Active", GeneralLedgerAccountState.Active),
            new NameStringViewModel("Deleted", GeneralLedgerAccountState.Deleted)
        ]
    }

    static scheduleTypeOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Fixed", ScheduleType.Fixed),
            new NameStringViewModel("Dynamic", ScheduleType.Dynamic)
        ]
    }

    static scheduleDueDatesTypeOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Interval", ScheduleDueDatesType.Interval),
            new NameStringViewModel("Fixed Days Of Month", ScheduleDueDatesType.FixedDaysOfMonth)
        ]
    }

    static scheduleFrequencyOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Once", ScheduleFrequency.Once),
            new NameStringViewModel("Daily", ScheduleFrequency.Daily),
            new NameStringViewModel("Weekly", ScheduleFrequency.Weekly),
            new NameStringViewModel("Two Weekly", ScheduleFrequency.TwoWeekly),
            new NameStringViewModel("Monthly", ScheduleFrequency.Monthly),
            new NameStringViewModel("Quarterly", ScheduleFrequency.Quarterly),
            new NameStringViewModel("Annually", ScheduleFrequency.Annually),
        ]
    }

    static transactionsOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Cleared", TransactionsOptions.Cleared),
            new NameStringViewModel("Pending", TransactionsOptions.Pending)
        ]
    }

    static transactionsOrderOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Display Date", TransactionsOrderOptions.DisplayDate),
            new NameStringViewModel("Timestamp", TransactionsOrderOptions.Timestamp)
        ]
    }

    static productTypeOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Deposit", ProductType.Deposit),
            new NameStringViewModel("Lending", ProductType.Lending),
        ]
    }

    static productStateOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Pending", ProductState.Pending),
            new NameStringViewModel("Published", ProductState.Published),
            new NameStringViewModel("Withdrawn", ProductState.Withdrawn)
        ]
    }

    static productIssueStateOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Pending", ProductIssueState.Pending),
            new NameStringViewModel("Published", ProductIssueState.Published),
            new NameStringViewModel("Rejected", ProductIssueState.Rejected)
        ]
    }

    static paymentOrderPaymentStateOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Completed", PaymentState.Completed),
            new NameStringViewModel("Scheduled", PaymentState.Scheduled),
            new NameStringViewModel("Processing", PaymentState.Processing),
            new NameStringViewModel("Pending Approval", PaymentState.PendingApproval),
            new NameStringViewModel("Submitted", PaymentState.Submitted),
            new NameStringViewModel("Accepted", PaymentState.Accepted),
            new NameStringViewModel("Rejected", PaymentState.Rejected),
            new NameStringViewModel("User Rejected", PaymentState.UserRejected),
            new NameStringViewModel("User Cancelled", PaymentState.UserCancelled),
            new NameStringViewModel("Failed", PaymentState.Failed),
            new NameStringViewModel("Monitoring Held", PaymentState.MonitoringHeld),
            new NameStringViewModel("Monitoring Rejected", PaymentState.MonitoringRejected)
        ]
    }

    static nonWorkingDaysOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Next Working Day", NonWorkingDays.NextWorkingDay),
            new NameStringViewModel("Previous Working Day", NonWorkingDays.PreviousWorkingDay),
            new NameStringViewModel("Do Not Reschedule", NonWorkingDays.DoNotReschedule),
        ]
    }

    static planLengthOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("3 months", PlanLength.ThreeMonths),
            new NameStringViewModel("6 months", PlanLength.SixMonths),
            new NameStringViewModel("12 months", PlanLength.TwelveMonths),
        ]
    }

    static dayOfWeekOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Monday", DayOfWeek.Monday),
            new NameStringViewModel("Tuesday", DayOfWeek.Thursday),
            new NameStringViewModel("Wednesday", DayOfWeek.Wednesday),
            new NameStringViewModel("Thursday", DayOfWeek.Thursday),
            new NameStringViewModel("Friday", DayOfWeek.Friday),
            new NameStringViewModel("Saturday", DayOfWeek.Saturday),
            new NameStringViewModel("Sunday", DayOfWeek.Sunday),
        ]
    }

    static dayOfMonthOptions(): NameNumberViewModel[] {
        return [
            new NameNumberViewModel("1st", 1),
            new NameNumberViewModel("2nd", 2),
            new NameNumberViewModel("3rd", 3),
            new NameNumberViewModel("4th", 4),
            new NameNumberViewModel("5th", 5),
            new NameNumberViewModel("6th", 6),
            new NameNumberViewModel("7th", 7),
            new NameNumberViewModel("8th", 8),
            new NameNumberViewModel("9th", 9),
            new NameNumberViewModel("10th", 10),
            new NameNumberViewModel("11th", 11),
            new NameNumberViewModel("12th", 12),
            new NameNumberViewModel("13th", 13),
            new NameNumberViewModel("14th", 14),
            new NameNumberViewModel("15th", 15),
            new NameNumberViewModel("16th", 16),
            new NameNumberViewModel("17th", 17),
            new NameNumberViewModel("18th", 18),
            new NameNumberViewModel("19th", 19),
            new NameNumberViewModel("20th", 20),
            new NameNumberViewModel("21st", 21),
            new NameNumberViewModel("22nd", 22),
            new NameNumberViewModel("23rd", 23),
            new NameNumberViewModel("24th", 24),
            new NameNumberViewModel("25th", 25),
            new NameNumberViewModel("26th", 26),
            new NameNumberViewModel("27th", 27),
            new NameNumberViewModel("28th", 28),
            new NameNumberViewModel("Last Day Of Month", -1),

        ]
    }

    static monthOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("January", Month.January),
            new NameStringViewModel("February", Month.February),
            new NameStringViewModel("March", Month.March),
            new NameStringViewModel("April", Month.April),
            new NameStringViewModel("March", Month.March),
            new NameStringViewModel("June", Month.June),
            new NameStringViewModel("July", Month.July),
            new NameStringViewModel("August", Month.August),
            new NameStringViewModel("September", Month.September),
            new NameStringViewModel("October", Month.October),
            new NameStringViewModel("November", Month.November),
            new NameStringViewModel("December", Month.December),
        ]
    }

    static shortMonthHandlingTypeOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Last Day Of Month", ShortMonthHandlingType.LastDayOfMonth),
            new NameStringViewModel("First Day Of Month", ShortMonthHandlingType.FirstDayOfMonth),
        ]
    }

    static appMediumTypes(fiEnabled: boolean, uxEnabled: boolean): NameStringViewModel[] {
       const types = [];
        if (fiEnabled) {
            types.push(new NameStringViewModel("FI API Service Account", AppMediumType.FiApiServiceAccount));
        }
        if (uxEnabled){
            types.push(new NameStringViewModel("UX API Service Account", AppMediumType.UxApiServiceAccount));
            types.push(new NameStringViewModel("UX API Customer Access", AppMediumType.UxApiCustomerAccess));
        }
        return types;
    }

    static countryOptions() {
        const list = countries.getNames('en', { select: 'official' })
        return Object.keys(list).map((key) => ({ value: key, name: list[key] }))
    }

    static feeDefinitionTypeOptions(): NameNumberViewModel[] {
        return [
            new NameNumberViewModel("Manual", FeeDefinitionType.Manual),
            new NameNumberViewModel("Monthly", FeeDefinitionType.Monthly)
        ]
    }

    static transactionTypeOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("EUROSIC_INBOUND_PAYMENT", TransactionType.eurosicInboundPayment),
            new NameStringViewModel("EUROSIC_OUTBOUND_PAYMENT", TransactionType.eurosicOutboundPayment),
            new NameStringViewModel("SIC_INBOUND_PAYMENT", TransactionType.sicInboundPayment),
            new NameStringViewModel("SIC_OUTBOUND_PAYMENT", TransactionType.sicOutboundPayment),
            new NameStringViewModel("SIC_SETTLEMENT", TransactionType.sicSettlement),
            new NameStringViewModel("FPS_INBOUND_PAYMENT", TransactionType.fpsInboundPayment),
            new NameStringViewModel("FPS_OUTBOUND_PAYMENT", TransactionType.fpsOutboundPayment),
            new NameStringViewModel("CHAPS_INBOUND_PAYMENT", TransactionType.chapsInboundPayment),
            new NameStringViewModel("CHAPS_OUTBOUND_PAYMENT", TransactionType.chapsOutboundPayment),
            new NameStringViewModel("SWIFT_INBOUND_PAYMENT", TransactionType.swiftInboundPayment),
            new NameStringViewModel("SWIFT_OUTBOUND_PAYMENT", TransactionType.swiftOutboundPayment),
            new NameStringViewModel("BACS_INBOUND_PAYMENT", TransactionType.bacsInboundPayment),
            new NameStringViewModel("BACS_OUTBOUND_PAYMENT", TransactionType.bacsOutboundPayment),
            new NameStringViewModel("SEPA_INBOUND_PAYMENT", TransactionType.sepaInboundPayment),
            new NameStringViewModel("SEPA_OUTBOUND_PAYMENT", TransactionType.sepaOutboundPayment),
            new NameStringViewModel("CHARGEBACK", TransactionType.chargeback),
            new NameStringViewModel("DOMESTIC_ATM", TransactionType.domesticAtm),
            new NameStringViewModel("DOMESTIC_ATM_FX", TransactionType.domesticAtmFx),
            new NameStringViewModel("EURO_ATM", TransactionType.euroAtm),
            new NameStringViewModel("INTERNATIONAL_ATM", TransactionType.internationalAtm),
            new NameStringViewModel("DOMESTIC_POS", TransactionType.domesticPos),
            new NameStringViewModel("DOMESTIC_POS_FX", TransactionType.domesticPosFx),
            new NameStringViewModel("EURO_POS", TransactionType.euroPos),
            new NameStringViewModel("INTERNATIONAL_POS", TransactionType.internationalPos),
            new NameStringViewModel("DOMESTIC_ECOM", TransactionType.domesticEcom),
            new NameStringViewModel("DOMESTIC_ECOM_FX", TransactionType.domesticEcomFx),
            new NameStringViewModel("EURO_ECOM", TransactionType.euroEcom),
            new NameStringViewModel("INTERNATIONAL_ECOM", TransactionType.internationalEcom),
            new NameStringViewModel("REWARD", TransactionType.reward),
            new NameStringViewModel("DOMESTIC_MOTO", TransactionType.domesticMoto),
            new NameStringViewModel("DOMESTIC_MOTO_FX", TransactionType.domesticMotoFx),
            new NameStringViewModel("EURO_MOTO", TransactionType.euroMoto),
            new NameStringViewModel("INTERNATIONAL_MOTO", TransactionType.internationalMoto),
        ]
    }

    static productFeatureVisibility(productSubType: ProductSubType) {
        const visiblity = new ProductFeatureVisiblity();
        // All values default to false
        switch (productSubType) {
            case ProductSubType.DepositCurrentAccount:
                visiblity.depositBankSettings = true;
                visiblity.depositCardSettings = true;
                visiblity.depositPaymentOrderSettings = true;
                visiblity.depositInterestSettings = true;
                visiblity.depositOverdraftSettings = true;
                visiblity.depositOverdraftInterestSettings = true;
                break;
            case ProductSubType.DepositFixedTerm:
                visiblity.generalPayeeSettings = true;
                visiblity.depositBankSettings = true;
                visiblity.depositPaymentOrderSettings = true;
                visiblity.depositInterestSettings = true;
                visiblity.depositFixedTermSettings = true;
                visiblity.depositFixedTermMaturitySettings = true;
                break;
            case ProductSubType.DepositNoticeAccount:
                visiblity.generalPayeeSettings = true;
                visiblity.depositBankSettings = true;
                visiblity.depositPaymentOrderSettings = true;
                visiblity.depositInterestSettings = true;
                visiblity.depositInstantAccessSettings = true;
                visiblity.depositCoolingOffSettings = true;
                break;
            case ProductSubType.DepositSavingsAccount:
                visiblity.depositBankSettings = true;
                visiblity.depositCardSettings = true;
                visiblity.depositPaymentOrderSettings = true;
                visiblity.depositInterestSettings = true;
                break;
            case ProductSubType.DepositIndividualSavingsAccount:
                visiblity.depositPaymentOrderSettings = true;
                visiblity.depositInterestSettings = true;
                break;
            case ProductSubType.LendingFixedTerm:
                visiblity.generalPayeeSettings = true;
                visiblity.lendingBankSettings = true;
                visiblity.lendingInterestSettings = true;
                visiblity.lendingScheduleSettings = true;
                visiblity.lendingTermSettings = true;
                break;
            case ProductSubType.LendingDynamicTerm:
                visiblity.lendingBankSettings = true;
                visiblity.lendingInterestSettings = true;
                visiblity.lendingDynamicTermSettings = true;
                break;
            case ProductSubType.LendingRevolvingCredit:
                visiblity.generalPayeeSettings = true;
                visiblity.lendingBankSettings = true;
                visiblity.lendingCardSettings = true;
                visiblity.lendingInterestSettings = true;
                visiblity.lendingRevolvingCreditSettings = true;
                visiblity.lendingRepaymentPlanSettings = true;
                visiblity.lendingRepaymentPlanInterestSettings = true
                break;
        }
        return visiblity;
    }

    static targetCustomerTypeOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Retail", TargetCustomerType.Retail),
            new NameStringViewModel("Commercial", TargetCustomerType.Commercial),
        ]
    }

    // Deposit Product Utils

    static depositProductSubTypeOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Current Account", ProductSubType.DepositCurrentAccount),
            new NameStringViewModel("Savings Account", ProductSubType.DepositSavingsAccount),
            new NameStringViewModel("Fixed Term", ProductSubType.DepositFixedTerm),
            new NameStringViewModel("Notice Account", ProductSubType.DepositNoticeAccount)
        ]
    }

    static fundingWindowTriggerTypeOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("First Deposit", FundingWindowTriggerType.FirstDeposit),
            // Note: Commented out other funding window trigger type option as it is not used at the moment
            // new NameStringViewModel("Minimum Balance Met", FundingWindowTriggerType.MinimumBalanceMet),
        ]
    }

    static fixedTermMaturityTypesForProductLevel(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Reinvest Full Balance", FixedTermMaturityType.ReinvestFullBalance),
            new NameStringViewModel("Withdraw Full Balance", FixedTermMaturityType.FullWithdrawal),
        ]
    }

    static fixedTermMaturityTypesForAccountLevel(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Reinvest Full Balance", FixedTermMaturityType.ReinvestFullBalance),
            new NameStringViewModel("Withdraw Full Balance", FixedTermMaturityType.FullWithdrawal),
            // Note: Commented out this maturity type option as it is not used at the moment
            //new NameStringViewModel("Interest Only Withdrawal", FixedTermMaturityType.InterestOnlyWithdrawal),
            new NameStringViewModel("Custom", FixedTermMaturityType.Custom)
        ]
    }

    // Lending Product Utils

    static lendingProductSubTypeOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Fixed Term", ProductSubType.LendingFixedTerm),
            new NameStringViewModel("Dynamic Term", ProductSubType.LendingDynamicTerm),
            new NameStringViewModel("Interest Free", ProductSubType.LendingInterestFree),
            new NameStringViewModel("Revolving Credit", ProductSubType.LendingRevolvingCredit),
        ]
    }

    static postedFrequencyTypeOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Disbursement", PostedFrequencyType.Disbursement),
            new NameStringViewModel("Repayment", PostedFrequencyType.Repayment),
        ]
    }

    // Interest Utils

    static interestTypeOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Simple Interest", InterestType.Simple),
            new NameStringViewModel("Compound Interest", InterestType.Compound)
        ]
    }

    static interestRepaymentTypeOptions(): NameNumberViewModel[] {
        return [
            new NameNumberViewModel("Interest Only", InterestRepaymentType.InterestOnly),
            new NameNumberViewModel("Captial & Interest", InterestRepaymentType.CaptialAndInterest),
        ]
    }

    static interestBaseRateOptions(): NameNumberViewModel[] {
        return [
            new NameNumberViewModel("BOE", InterestBaseRate.BOE),
            new NameNumberViewModel("SONIA", InterestBaseRate.SONIA),
        ]
    }

    static interestRateTypeOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Fixed", InterestRateType.Fixed),
            new NameStringViewModel("Variable", InterestRateType.Variable),
        ]
    }

    static interestDaysInYearOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Actual/365", InterestDaysInYear.Actual365),
            new NameStringViewModel("Actual/360", InterestDaysInYear.Actual360),
        ]
    }

    static interestCalculationTypeOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Flat", InterestCalculationType.Flat),
            new NameStringViewModel("Declining Balance", InterestCalculationType.DecliningBalance),
            new NameStringViewModel("Declining Balance (Equal Installments)", InterestCalculationType.DecliningBalanceEqualInstallments),
        ]
    }

    static interestCalculatedOnOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Average Balance", InterestCalculatedOnBalanceType.AverageDailyBalance),
            new NameStringViewModel("Minimum Balance", InterestCalculatedOnBalanceType.MinimumBalance),
            new NameStringViewModel("End Of Day Balance", InterestCalculatedOnBalanceType.EndOfDayBalance),
        ]
    }

    static interestPostedTypeOptions(): NameNumberViewModel[] {
        return [
            new NameNumberViewModel("Maturity", InterestPostedOnType.Maturity),
            new NameNumberViewModel("Every week", InterestPostedOnType.EveryWeek),
            new NameNumberViewModel("Every month", InterestPostedOnType.EveryMonth),
            new NameNumberViewModel("First day of every month", InterestPostedOnType.FirstDayOfEveryMonth),
            new NameNumberViewModel("Every three months", InterestPostedOnType.EveryThreeMonths),
            new NameNumberViewModel("Every six months", InterestPostedOnType.EverySixMonths),
            new NameNumberViewModel("Every year", InterestPostedOnType.EveryYear),
        ]
    }

    static repaymentInterestCalculationTypeOptions(): NameStringViewModel[] {
        return [
            new NameStringViewModel("Repayment Periodocity", RepaymentInterestCalculationType.RepaymentPeriodocity),
            new NameStringViewModel("Actual Days", RepaymentInterestCalculationType.ActualDays),
        ]
    }

}